// react
import React, { useMemo } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useNavigate, useParams } from '@tanstack/react-router';

// material ui icons
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import AssessmentRoundedIcon from '@mui/icons-material/AssessmentRounded';
import BoltIcon from '@mui/icons-material/Bolt';
import DashboardRoundedIcon from '@mui/icons-material/DashboardRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import LanRoundedIcon from '@mui/icons-material/LanRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import WaterDropRoundedIcon from '@mui/icons-material/WaterDropRounded';

// material ui
import {
  alpha,
  Avatar,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Grid,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// api
import { useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';
import { useGetSubscriptionSubscriptionAccountIdGetQuery } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysisSubscription';
import { useGetEnergyGroupsQuery } from '@/api/generated/recalc/endpoints/v2/energyGroups';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import CardHeaderWithIcon from '@/components/shared/surfaces/card/CardHeaderWithIcon';

// feature-components
import AlarmCard from '@/features/energyxplorer/components/sales-cards/AlarmCard';
import EnergyXplorerCard from '@/features/energyxplorer/components/sales-cards/EnergyXplorerCard';
import PortfolioCard from '@/features/energyxplorer/components/sales-cards/PortfolioCard';

// components
import { useSessionAuth } from '@/contexts/SessionAuthContext';
import { useHasService } from '@/hooks/useHasService';
import { formatNumber } from '@/utils/formatters';

// relative
import EnergyGroupDataGrid from './energygroup/EnergyGroupDataGrid';

type PortfolioSubscription =
  | { power?: boolean; water?: boolean; heat?: boolean }
  | undefined;

interface QuickAccessCard {
  title: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  path: string;
  enabled: boolean | undefined;
  consumption: number | undefined;
  unit: string;
  category: string;
}

interface AnalysisCard {
  title: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  path: string;
  enabled: boolean;
  badge?: string | null;
  disabled?: boolean;
  category: string;
}

interface AlarmCard {
  title: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  path?: string;
  enabled?: boolean;
  badge?: string;
  disabled?: boolean;
}

const PortfolioPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation('energy-analysis');

  const { accountId } = useParams({ strict: false });

  const { data: energyGroups } = useGetEnergyGroupsQuery(
    { accountIds: accountId ? [Number(accountId)] : undefined },
    {
      skip: !accountId,
    },
  );

  const { hasService: hasPortfolioAnalysisService } =
    useHasService('PortfolioAnalysis');

  const { hasService: hasEnergyXplorerService } =
    useHasService('EnergyXplorer');

  const { hasService: hasEnergyAlarmsService } = useHasService('EnergyAlarms');

  const { isAdmin } = useSessionAuth();

  const showPortfolioAnalysis = hasPortfolioAnalysisService;
  const showEnergyXplorer = hasEnergyXplorerService;
  const showEnergyAlarms = hasEnergyAlarmsService;

  const { data: subscriptionData, isLoading: isSubscriptionLoading } =
    useGetSubscriptionSubscriptionAccountIdGetQuery(
      { accountId: accountId as unknown as number },
      {
        skip: !accountId || (!hasPortfolioAnalysisService && !isAdmin),
      },
    );

  const typedSubscription = subscriptionData as PortfolioSubscription;

  // Show warning if no subscription
  // Fetch portfolio data for each energy type
  const { data: powerData, isLoading: isPowerLoading } =
    useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
      { accountId: accountId as unknown as number, analysisType: 'power' },
      { skip: !accountId || !typedSubscription?.power },
    );

  const { data: waterData, isLoading: isWaterLoading } =
    useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
      { accountId: accountId as unknown as number, analysisType: 'water' },
      { skip: !accountId || !typedSubscription?.water },
    );

  const { data: heatData, isLoading: isHeatLoading } =
    useGetPortfolioAnalysisPortfolioAnalysisAnalysisTypeAccountIdGetQuery(
      { accountId: accountId as unknown as number, analysisType: 'heat' },
      { skip: !accountId || !typedSubscription?.heat },
    );

  // Calculate statistics
  const portfolioStats = useMemo(() => {
    if (!typedSubscription) return null;

    const totalBuildings = new Set<string>();
    const totalArea = { power: 0, heat: 0, water: 0 };
    const totalConsumption = { power: 0, heat: 0, water: 0 };

    if (powerData?.portfolio_analysis_list) {
      powerData.portfolio_analysis_list.forEach((item) => {
        if (item.type_of_mp !== 'PV') {
          totalBuildings.add(item.main_address);
          totalArea.power += item.SumBuildingArea || 0;
          totalConsumption.power += item.total_consumption_year || 0;
        }
      });
    }

    if (heatData?.portfolio_analysis_list) {
      heatData.portfolio_analysis_list.forEach((item) => {
        totalBuildings.add(item.main_address);
        totalArea.heat += item.SumBuildingArea || 0;
        totalConsumption.heat += item.total_consumption_year || 0;
      });
    }

    if (waterData?.portfolio_analysis_list) {
      waterData.portfolio_analysis_list.forEach((item) => {
        totalBuildings.add(item.main_address);
        totalArea.water += item.SumBuildingArea || 0;
        totalConsumption.water += item.total_consumption_year || 0;
      });
    }

    return {
      totalBuildings: totalBuildings.size,
      totalArea: totalArea.power + totalArea.heat + totalArea.water,
      totalConsumption,
    };
  }, [powerData, heatData, waterData, typedSubscription]);

  const isLoading =
    isSubscriptionLoading || isPowerLoading || isWaterLoading || isHeatLoading;

  // Quick access cards configuration
  const quickAccessCards: QuickAccessCard[] = [
    {
      title: t('portfolio.cards.power.title'),
      description: t('portfolio.cards.power.description'),
      icon: <BoltIcon fontSize="large" />,
      color: theme.palette.power?.main || theme.palette.primary.main,
      path: 'energyxplorer/portfolio/power/analysis/overview',
      enabled: typedSubscription?.power,
      consumption: portfolioStats?.totalConsumption.power,
      unit: 'kWh',
      category: 'energy',
    },
    {
      title: t('portfolio.cards.heat.title'),
      description: t('portfolio.cards.heat.description'),
      icon: <LocalFireDepartmentRoundedIcon fontSize="large" />,
      color: theme.palette.heat?.main || theme.palette.error.main,
      path: 'energyxplorer/portfolio/heat/analysis/overview',
      enabled: typedSubscription?.heat,
      consumption: portfolioStats?.totalConsumption.heat,
      unit: 'kWh',
      category: 'energy',
    },
    {
      title: t('portfolio.cards.water.title'),
      description: t('portfolio.cards.water.description'),
      icon: <WaterDropRoundedIcon fontSize="large" />,
      color: theme.palette.water?.main || theme.palette.info.main,
      path: 'energyxplorer/portfolio/water/analysis/overview',
      enabled: typedSubscription?.water,
      consumption: portfolioStats?.totalConsumption.water,
      unit: 'm³',
      category: 'energy',
    },
  ];

  const analysisCards: AnalysisCard[] = [
    {
      title: t('portfolio.cards.portfolio_overview.title'),
      description: t('portfolio.cards.portfolio_overview.description'),
      icon: <HomeWorkRoundedIcon fontSize="large" />,
      color: theme.palette.primary.main,
      path: 'energyxplorer/portfolio/overview',
      enabled: true,
      category: 'analysis',
    },
    // {
    //   title: t('portfolio.cards.building_analysis.title'),
    //   description: t('portfolio.cards.building_analysis.description'),
    //   icon: <ApartmentRoundedIcon fontSize="large" />,
    //   color: theme.palette.secondary.main,
    //   path: 'energyanalysis/portfolio/building',
    //   enabled: true,
    //   category: 'analysis',
    // },
  ];

  const alarmCards: AlarmCard[] = [
    {
      title: t('portfolio.cards.baseload_deviation.title'),
      description: t('portfolio.cards.baseload_deviation.description'),
      icon: <AssessmentRoundedIcon fontSize="large" />,
      color: theme.palette.warning.main,
      path: 'alarms',
      enabled: true,
    },
    {
      title: t('portfolio.cards.powerload_distribution.title'),
      description: t('portfolio.cards.powerload_distribution.description'),
      icon: <InsightsRoundedIcon fontSize="large" />,
      color: theme.palette.success.main,
      path: 'alarms',
      enabled: true,
    },
    {
      title: t('portfolio.cards.anomaly_trend.title'),
      description: t('portfolio.cards.anomaly_trend.description'),
      icon: <AccountTreeRoundedIcon fontSize="large" />,
      color: theme.palette.info.main,
      path: 'alarms',
      enabled: true,
    },
    {
      title: t('portfolio.cards.peak_shift.title'),
      description: t('portfolio.cards.peak_shift.description'),
      icon: <DashboardRoundedIcon fontSize="large" />,
      color: theme.palette.error.main,
      badge: t('portfolio.status.coming_soon'),
      disabled: true,
    },
  ];

  const handleCardClick = (
    path: string | undefined,
    enabled: boolean | undefined,
    disabled: boolean | undefined,
  ) => {
    if (!enabled || disabled) return;
    navigate({ to: `/${accountId}/${path}` });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid
              key={i}
              size={{
                xs: 12,
                sm: 6,
                md: 4
              }}>
              <Skeleton variant="rectangular" height={200} />
            </Grid>
          ))}
        </Grid>
      </PageContainer>
    );
  }

  const filteredEnergyGroups = energyGroups?.filter(
    (eg) => !eg.name?.toLowerCase().includes('portfolio'),
  );

  const energyGroupInitiativeSum = filteredEnergyGroups?.reduce(
    (sum, group) => sum + (group.energyInitiatives.length || 0),
    0,
  );

  const energyGroupTreeNodesSum = filteredEnergyGroups?.reduce(
    (sum, group) => sum + (group.energyTreeNodes.length || 0),
    0,
  );

  return (
    <PageContainer>
      <PageHeader
        title={t('portfolio.portfolio_analysis')}
        subtitle={t('portfolio.subtitle')}
        disableArrow
      />

      {/* Energy Type Cards - Show Portfolio Analysis first if available */}
      {showPortfolioAnalysis && (
        <Stack>
          <Grid container spacing={2}>
            {quickAccessCards.map((card, index) => (
              <Grid
                key={index}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 4
                }}>
                <Card
                  variant="outlined"
                  sx={{
                    cursor:
                      card.enabled && !card.disabled ? 'pointer' : 'default',
                    opacity: card.enabled ? 1 : 0.5,
                  }}
                >
                  <CardActionArea
                    onClick={() => handleCardClick(card.path, true, false)}
                    sx={{
                      height: '100%',
                      '&:hover': {
                        backgroundColor: 'action.selectedHover',
                      },
                    }}
                  >
                    <CardHeader
                      title={card.title}
                      subheader={card.description}
                      avatar={
                        <Avatar
                          variant="rounded"
                          sx={{
                            bgcolor: alpha(card.color, 0.1),
                            borderRadius: 3,
                          }}
                        >
                          {card.icon &&
                            React.cloneElement(card.icon, {
                              sx: {
                                verticalAlign: 'middle',
                                color: card.color,
                              },
                            })}
                        </Avatar>
                      }
                      slotProps={{
                        title: {
                          variant: 'subtitle1',
                          fontWeight: 600,
                        }
                      }}
                    />
                    <CardContent>
                      {card.enabled && card.consumption !== undefined && (
                        <Box>
                          <Typography variant="h4" fontWeight="bold">
                            {formatNumber(card.consumption, 0)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {card.unit} {t('portfolio.status.per_year')}
                          </Typography>
                        </Box>
                      )}
                      {!card.enabled && (
                        <Typography variant="caption" color="text.secondary">
                          {t('portfolio.status.not_subscribed')}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
            <Grid
              size={{
                xs: 12,
                sm: 6,
                md: 12
              }}>
              <List dense>
                {analysisCards.map((card, index) => (
                  <ListItemButton
                    key={index}
                    component={Card}
                    onClick={() =>
                      handleCardClick(card.path, card.enabled, card.disabled)
                    }
                    sx={{
                      borderRadius: 1.5,
                      alignItems: 'flex-start',
                      backgroundColor: (theme) =>
                        theme.palette.mode === 'light'
                          ? theme.palette.background.paper
                          : theme.palette.grey[800],
                      mb: 1,
                      pr: 4,
                      '&:hover': {
                        '& .end-icon': {
                          transform: 'translateX(4px)',
                          opacity: 1,
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: card.color,
                        mt: 1.25,
                      }}
                    >
                      {card.icon}
                    </ListItemIcon>

                    {/* Main text */}
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={700}>
                          {card.title}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          // sx={{ lineHeight: 1.4 }}
                        >
                          {card.description}
                        </Typography>
                      }
                    />

                    {/* Right-side arrow */}
                    <Box
                      className="end-icon"
                      sx={{
                        mt: 1.75,
                        color: 'text.secondary',
                        opacity: 0.6,
                        transition: 'transform 180ms ease, opacity 180ms ease',
                      }}
                    >
                      <ArrowForwardRoundedIcon />
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            </Grid>
          </Grid>
        </Stack>
      )}
      {showEnergyXplorer && (
        <Box mb={2}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t('portfolio_energyxplorer.title')}
          </Typography>
          <Grid container spacing={2} mb={2}>
            <Grid size={6}>
              <Card variant="outlined">
                <CardHeader
                  title={t(
                    'portfolio_energyxplorer.cards.measurement_points.title',
                  )}
                  subheader={t(
                    'portfolio_energyxplorer.cards.measurement_points.description',
                  )}
                  avatar={
                    <Avatar
                      variant="rounded"
                      sx={{
                        bgcolor: alpha(theme.palette.remoni[700], 0.1),
                        borderRadius: 3,
                      }}
                    >
                      <LanRoundedIcon
                        sx={{ color: theme.palette.remoni[700] }}
                      />
                    </Avatar>
                  }
                  slotProps={{
                    title: {
                      variant: 'subtitle1',
                      fontWeight: 600,
                    }
                  }}
                />
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(energyGroupTreeNodesSum, 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('portfolio_energyxplorer.cards.across_energy_groups', {
                      count: filteredEnergyGroups?.length,
                    })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={6}>
              <Card variant="outlined">
                <CardHeader
                  title={t('portfolio_energyxplorer.cards.initiatives.title')}
                  subheader={t(
                    'portfolio_energyxplorer.cards.initiatives.description',
                  )}
                  avatar={
                    <Avatar
                      variant="rounded"
                      sx={{
                        bgcolor: alpha(theme.palette.remoni[700], 0.1),
                        borderRadius: 3,
                      }}
                    >
                      <EnergySavingsLeafRoundedIcon
                        sx={{ color: theme.palette.remoni[700] }}
                      />
                    </Avatar>
                  }
                  slotProps={{
                    title: {
                      variant: 'subtitle1',
                      fontWeight: 600,
                    }
                  }}
                />
                <CardContent>
                  <Typography variant="h4" fontWeight="bold">
                    {formatNumber(energyGroupInitiativeSum, 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('portfolio_energyxplorer.cards.across_energy_groups', {
                      count: filteredEnergyGroups?.length,
                    })}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          <EnergyGroupDataGrid energyGroups={energyGroups} />
        </Box>
      )}

      {/* Analysis & Tools Cards - Show EnergyAlarms third if available */}
      {showEnergyAlarms && (
        <Box mb={4}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            {t('portfolio.analysis_tools')}
          </Typography>
          <Grid container spacing={3}>
            {alarmCards.map((card, index) => (
              <Grid
                key={index}
                size={{
                  xs: 12,
                  sm: 6,
                  md: 3
                }}>
                <Card
                  onClick={() =>
                    handleCardClick(card.path, card.enabled, card.disabled)
                  }
                  sx={{
                    height: '100%',
                    cursor:
                      card.enabled && !card.disabled ? 'pointer' : 'default',
                    opacity: card.disabled ? 0.5 : 1,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow:
                        card.enabled && !card.disabled
                          ? '0 8px 24px rgba(0,0,0,0.12)'
                          : 'none',
                      transform:
                        card.enabled && !card.disabled
                          ? 'translateY(-4px)'
                          : 'none',
                    },
                  }}
                >
                  <CardContent>
                    <CardHeaderWithIcon
                      icon={card.icon}
                      iconColor={card.color}
                      iconBgColor={alpha(card.color, 0.1)}
                      title={card.title}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Show sales/upgrade cards for unavailable services at the bottom */}
      <Grid container spacing={2}>
        {!showPortfolioAnalysis && (
          <Grid size="grow">
            <Box height="100%">
              <PortfolioCard />
            </Box>
          </Grid>
        )}

        {!showEnergyXplorer && (
          <Grid size="grow">
            <Box height="100%">
              <EnergyXplorerCard />
            </Box>
          </Grid>
        )}

        {!showEnergyAlarms && (
          <Grid size="grow">
            <Box height="100%">
              <AlarmCard />
            </Box>
          </Grid>
        )}
      </Grid>
    </PageContainer>
  );
};

export default PortfolioPage;
